// REQ-P1-06b: storage/public asset endpoints through the generated API client.

import createClient from 'openapi-fetch';
import { api, requestApiJson, type paths } from './client';
import { ApiStatusError } from './templates';

type PublicFlyerHighlightsResponse = NonNullable<
  paths['/public/flyers/highlights']['get']['responses'][200]['content']
>['application/json'];
type PublicFlyerHighlightsQuery = NonNullable<
  paths['/public/flyers/highlights']['get']['parameters']['query']
>;
type StoragePresignPutRequest = NonNullable<
  paths['/storage/presign-put']['post']['requestBody']
>['content']['application/json'];
type StoragePresignPutResponse = NonNullable<
  paths['/storage/presign-put']['post']['responses'][200]['content']
>['application/json'];

const PUBLIC_FLYER_PATH = '/public/flyers/highlights';
const API_PUBLIC_FLYER_PATH = `/api${PUBLIC_FLYER_PATH}`;

function trimTrailingSlash(value = ''): string {
  return String(value).replace(/\/+$/, '');
}

function createPublicFlyerClient(endpointOverride = '') {
  const endpoint = trimTrailingSlash(endpointOverride.split('?')[0] || '');
  if (!endpoint) return api;

  const apiPathIndex = endpoint.indexOf(API_PUBLIC_FLYER_PATH);
  if (apiPathIndex >= 0) {
    return createClient<paths>({
      baseUrl: endpoint.slice(0, apiPathIndex + '/api'.length),
      credentials: 'include',
    });
  }

  const publicPathIndex = endpoint.indexOf(PUBLIC_FLYER_PATH);
  if (publicPathIndex >= 0) {
    return createClient<paths>({
      baseUrl: endpoint.slice(0, publicPathIndex),
      credentials: 'include',
    });
  }

  return api;
}

export async function listPublicFlyerHighlights(
  limit: PublicFlyerHighlightsQuery['limit'],
  endpointOverride = ''
): Promise<PublicFlyerHighlightsResponse['flyers']> {
  const client = createPublicFlyerClient(endpointOverride);
  const { data, response } = await client.GET('/public/flyers/highlights', {
    params: { query: { limit } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data.flyers) ? data.flyers : [];
}

export async function createStoragePresignPut(
  body: StoragePresignPutRequest
): Promise<StoragePresignPutResponse> {
  const { data, response } = await api.POST('/storage/presign-put', { body });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as StoragePresignPutResponse;
}

export async function createEventImagePresignPut(
  body: {
    eventUuid: string;
    fileName: string;
    contentType: string;
    byteSize: number;
  }
): Promise<{ url: string; key: string; method: string; headers?: Record<string, unknown>; maxSize?: number }> {
  const { data, error, response } = await requestApiJson('/storage/event-images/presign-put', {
    method: 'POST',
    body,
  });
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  if (!response.ok) {
    const statusError = new ApiStatusError(response.status);
    const messagePayload = error && typeof error === 'object' ? (error as Record<string, unknown>) : payload;
    statusError.message = typeof messagePayload?.message === 'string' ? messagePayload.message : statusError.message;
    throw statusError;
  }
  return payload as { url: string; key: string; method: string; headers?: Record<string, unknown>; maxSize?: number };
}

export async function uploadEventImageFile(
  body: {
    eventUuid: string;
    file: File;
  }
): Promise<{ key: string; fileName?: string | null; contentType?: string; byteSize?: number; method?: string; maxSize?: number }> {
  const formData = new FormData();
  formData.append('eventUuid', body.eventUuid);
  formData.append('file', body.file, body.file.name);
  const { data, error, response } = await requestApiJson('/storage/event-images/upload', {
    method: 'POST',
    body: formData,
  });
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  if (!response.ok) {
    const statusError = new ApiStatusError(response.status);
    const messagePayload = error && typeof error === 'object' ? (error as Record<string, unknown>) : payload;
    statusError.message = typeof messagePayload?.message === 'string' ? messagePayload.message : statusError.message;
    throw statusError;
  }
  if (typeof payload.key !== 'string' || !payload.key) {
    throw new Error('アップロード結果の取得に失敗しました。');
  }
  return payload as { key: string; fileName?: string | null; contentType?: string; byteSize?: number; method?: string; maxSize?: number };
}

export function uploadFileToPresignedUrl(
  url: string,
  file: File,
  headers: Record<string, unknown> = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    Object.entries(headers || {}).forEach(([key, value]) => {
      if (value != null) request.setRequestHeader(key, String(value));
    });
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed: HTTP ${request.status}`));
    };
    request.onerror = () => reject(new Error('Upload failed'));
    request.onabort = () => reject(new Error('Upload aborted'));
    request.ontimeout = () => reject(new Error('Upload timeout'));
    request.send(file);
  });
}

export function downloadBlobFromUrl(
  url: string,
  credentials: RequestCredentials = 'include'
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('GET', url);
    request.responseType = 'blob';
    request.withCredentials = credentials !== 'omit';
    request.onload = () => {
      if (request.status >= 200 && request.status < 300 && request.response instanceof Blob) {
        resolve(request.response);
        return;
      }
      reject(new Error(`Download failed: HTTP ${request.status}`));
    };
    request.onerror = () => reject(new Error('Download failed'));
    request.onabort = () => reject(new Error('Download aborted'));
    request.ontimeout = () => reject(new Error('Download timeout'));
    request.send();
  });
}
